// src/services/api.js
import axios from "axios";
import { DATA_SAVE_URL } from "../scenes/utils/UrlHandle";

const API_BASE_URL = DATA_SAVE_URL + "/api/v1";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 90000, // Add timeout
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried refreshing yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, add to queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh token
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
          throw new Error("No refresh token available");
        }

        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh-token`,
          {
            refreshToken,
          }
        );

        const { access_token, refresh_token } = response.data;

        // Store new tokens
        localStorage.setItem("token", access_token);
        if (refresh_token) {
          localStorage.setItem("refreshToken", refresh_token);
        }

        // Process queued requests
        processQueue(null, access_token);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to login
        processQueue(refreshError, null);
        localStorage.removeItem("token");
        localStorage.removeItem("user");

        // Redirect to login page if we're in a browser environment
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } // For other errors, provide better error messages
    if (error.response) {
      // Server responded with error status
      console.error("API Error:", error.response.status, error.response.data);
    } else if (error.request) {
      // Request made but no response received
      console.error("Network Error:", error.request);
    } else {
      // Something else happened
      console.error("Error:", error.message);
    }

    return Promise.reject(error);
  }
);

export const checkTokenValidity = () => {
  const token = localStorage.getItem("token");
  if (!token) return false;

  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.exp * 1000 > Date.now();
  } catch (error) {
    return false;
  }
};

export const authAPI = {
  login: (credentials) => api.post("/auth/authenticate", credentials),
  register: (userData) => api.post("/auth/register", userData),
};

export const publicAPI = {
  getHomeContent: () => api.get("/public/home"),
};

export const getPayload = {
  getPayloadData: (apiPath) => api.get(apiPath),
};

export const getPayloadWithParams = {
  setPayloadData: (apiPath, data = null, params = null) =>
    api.get(apiPath, data, { params }),
};

export const setPayload = {
  setPayloadData: (apiPath) => api.put(apiPath),
};

export const setPostPayload = {
  setPayloadData: (apiPath, payload = {}) => api.post(apiPath, payload),
};

export const setPutPayload = {
  setPayloadData: (apiPath, payload = {}) => api.put(apiPath, payload),
};

export const setDeletePayload = {
  setPayloadData: (apiPath, payload) => api.delete(apiPath, payload),
};

export const setPutPayloadWithParams = {
  setPayloadData: (apiPath, data = null, params = null) =>
    api.put(apiPath, data, { params }),
};

export default api;
